import { BarSeriesOption } from 'echarts';
import type { CallbackDataParams } from 'echarts/types/dist/shared';
import { EChartWrapper } from '../../../../../components/Charts/EchartWrapper';
import {
  KPIRequestStatus,
  kpiRequestStatusToColor,
} from '../../../../../data-models/kpi-requests.data-model';
import { FMT } from '../../../../../util/formatter-service';
import { NumericFormatterId } from '../../../../../util/formatters/NumericFormatters';
import { CompanyPeriodRequestStatus } from '../../../../../schemas/CompanyPeriodRequest.schema';

type KpiRequestChartProps = {
  data: Map<string, number>;
  allStatuses: (KPIRequestStatus | CompanyPeriodRequestStatus)[];
  selectedStatus: KPIRequestStatus | string;
};

type KpiChartDataItem = {
  value: number;
  statusName: string;
  count: number;
  unknownStatuses?: string[];
};

const BarBorderRadius = 5;

export function KpiRequestChart({ data, allStatuses, selectedStatus }: KpiRequestChartProps) {
  const series: BarSeriesOption[] = [];
  const values = [] as number[][];
  let total = 0;

  // Separate known and unknown statuses
  const knownStatuses: string[] = [];
  const unknownStatuses: string[] = [];
  let otherCount = 0;

  Array.from(data.keys())
    .filter((key) => key !== 'All')
    .forEach((key) => {
      if (allStatuses.includes(key as (typeof allStatuses)[number])) {
        knownStatuses.push(key);
      } else {
        unknownStatuses.push(key);
        otherCount += data.get(key) || 0;
      }
      total += data.get(key) || 0;
    });

  // Sort known statuses according to KpiStatusOrder
  const keysSorted = knownStatuses.sort((status1, status2) => {
    return (
      allStatuses.indexOf(status1 as (typeof allStatuses)[number]) -
      allStatuses.indexOf(status2 as (typeof allStatuses)[number])
    );
  });

  // Add "Other" category if there are unknown statuses
  if (unknownStatuses.length > 0) {
    keysSorted.push('Other');
  }

  // Prepare values array
  keysSorted.forEach((key) => {
    if (key === 'Other') {
      values.push([otherCount]);
    } else {
      const value = data.get(key) || 0;
      values.push([value]);
    }
  });

  keysSorted.forEach((status, seriesIndex) => {
    const count = values[seriesIndex][0];
    const seriesName = status === 'Other' ? 'Other' : FMT.format('kpiStatus', status as KPIRequestStatus);

    let borderStyle = [0, 0, 0, 0];
    if (keysSorted.length === 1) {
      // Only one series - round all corners
      borderStyle = [BarBorderRadius, BarBorderRadius, BarBorderRadius, BarBorderRadius];
    } else if (seriesIndex === 0) {
      borderStyle = [BarBorderRadius, 0, 0, BarBorderRadius];
    } else if (seriesIndex === keysSorted.length - 1) {
      borderStyle = [0, BarBorderRadius, BarBorderRadius, 0];
    }

    let barColor = status === 'Other' ? '#999999' : kpiRequestStatusToColor(status as KPIRequestStatus);
    const isSelected = selectedStatus === 'All' || selectedStatus === status;
    if (!isSelected) {
      barColor = 'rgba(0, 0, 0, 0.1)';
    }

    series.push({
      name: seriesName,
      data: [
        {
          value: count,
          statusName: seriesName,
          count: count,
          unknownStatuses: status === 'Other' ? unknownStatuses : undefined,
        } as KpiChartDataItem,
      ],
      emphasis: {
        focus: 'series',
        itemStyle: {
          borderWidth: 1,
          borderColor: '#ffffff',
          shadowBlur: 12,
          shadowOffsetY: 4,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
        },
      },
      itemStyle: {
        borderRadius: borderStyle,
      },
      color: barColor,
      type: 'bar',
      stack: 'total',
    });
  });

  return (
    <EChartWrapper
      notMerge={true}
      onChartReady={(chart) => {
        // Force resize after initialization to ensure proper width
        setTimeout(() => chart.resize(), 0);
      }}
      options={{
        tooltip: {
          trigger: 'item',
          appendToBody: true,
          formatter: (params: CallbackDataParams | CallbackDataParams[]) => {
            const singleParam = Array.isArray(params) ? params[0] : params;
            const data = singleParam?.data as KpiChartDataItem | undefined;
            if (data && data.statusName) {
              const percent = total > 0 ? (data.count / total) * 100 : 0;
              let tooltipText = `${data.statusName} ${FMT.format(NumericFormatterId.percent0Dp, percent)} (${data.count})`;
              if (data.unknownStatuses && data.unknownStatuses.length > 0) {
                tooltipText += `<br/><small>Includes: ${data.unknownStatuses.join(', ')}</small>`;
              }
              return tooltipText;
            }
            return '';
          },
          backgroundColor: 'rgba(50, 50, 50, 0.95)',
          borderColor: '#ffffff',
          borderWidth: 1,
          textStyle: {
            color: '#ffffff',
            fontWeight: 'bold',
            fontSize: 12,
          },
          padding: [8, 12],
        },
        grid: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
        xAxis: {
          type: 'value',
          min: 0,
          max: total,
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
        yAxis: {
          type: 'category',
          data: ['total'],
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
        series,
      }}
    />
  );
}
